import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { opportunitiesAPI } from '../services/api';
import { ArrowLeft, MapPin, Calendar, Building, Eye, GraduationCap, Briefcase, Award, TrendingUp, ExternalLink } from 'lucide-react';
import ShareButton from '../components/ShareButton';
import { Card, CardBody, Badge } from '../components/ui';
import { useAuth } from '../context/AuthContext';

interface OpportunityDetail {
  _id: string;
  title: string;
  slug: string;
  type: 'scholarship' | 'internship' | 'training' | 'grant';
  description: string;
  organization?: string;
  eligibility: string[];
  benefits: string[];
  location?: { country: string; city: string; remote: boolean };
  applicationLink?: string;
  deadline?: string;
  featured: boolean;
  views: number;
  bannerImage?: string;
  createdAt: string;
}

const OpportunityDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const canSeeViews = user?.role === 'admin' || user?.role === 'content-creator';
  const [opportunity, setOpportunity] = useState<OpportunityDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (slug) fetchOpportunity(); }, [slug]);

  const fetchOpportunity = async () => {
    try {
      setLoading(true);
      const response = await opportunitiesAPI.getBySlug(slug!);
      setOpportunity(response.data.data);
    } catch (error) {
      console.error('Error fetching opportunity:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    const icons: any = {
      scholarship: <GraduationCap className="w-5 h-5" />,
      internship: <Briefcase className="w-5 h-5" />,
      training: <TrendingUp className="w-5 h-5" />,
      grant: <Award className="w-5 h-5" />,
    };
    return icons[type] || <GraduationCap className="w-5 h-5" />;
  };

  const getTypeColor = (type: string) => {
    const colors: any = {
      scholarship: 'bg-blue-100 text-blue-700',
      internship: 'bg-green-100 text-green-700',
      training: 'bg-purple-100 text-purple-700',
      grant: 'bg-orange-100 text-orange-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Opportunity not found</h2>
          <Link to="/opportunities" className="text-blue-600 hover:underline">Back to opportunities</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/opportunities"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to all opportunities
        </Link>

        <Card className="overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500"></div>

          {/* Banner Image */}
          {opportunity.bannerImage && (
            <div className="w-full h-56 sm:h-72 overflow-hidden">
              <img
                src={opportunity.bannerImage}
                alt={opportunity.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <CardBody className="p-6 sm:p-8">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              <div className={`w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center ${getTypeColor(opportunity.type)}`}>
                {getTypeIcon(opportunity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full capitalize font-medium ${getTypeColor(opportunity.type)}`}>
                      {opportunity.type}
                    </span>
                    {opportunity.featured && <Badge variant="warning">Featured</Badge>}
                  </div>
                  <ShareButton
                    url={`/opportunities/${opportunity.slug}`}
                    title={opportunity.title}
                    description={opportunity.description}
                    align="right"
                  />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{opportunity.title}</h1>
              </div>
            </div>

            {opportunity.organization && (
              <div className="flex items-center gap-2 text-gray-600 mb-6 pb-6 border-b border-gray-100">
                <Building className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">{opportunity.organization}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-4 mb-8">
              {opportunity.location && (opportunity.location.city || opportunity.location.country) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-5 h-5 flex-shrink-0" />
                  <span>{[opportunity.location.city, opportunity.location.country].filter(Boolean).join(', ')}</span>
                </div>
              )}
              {opportunity.deadline && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-5 h-5 flex-shrink-0" />
                  <span>Deadline: {new Date(opportunity.deadline).toLocaleDateString()}</span>
                </div>
              )}
              {canSeeViews && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Eye className="w-5 h-5 flex-shrink-0" />
                  <span>{opportunity.views} views</span>
                </div>
              )}
            </div>

            <div className="prose max-w-none mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">{opportunity.description}</div>
            </div>

            {opportunity.eligibility?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Eligibility</h2>
                <ul className="space-y-2">
                  {opportunity.eligibility.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {opportunity.benefits?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Benefits</h2>
                <ul className="space-y-2">
                  {opportunity.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Bottom actions */}
            {opportunity.applicationLink && (
              <div className="pt-6 border-t border-gray-100">
                <a
                  href={opportunity.applicationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Apply Now
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default OpportunityDetail;
